import React, { useEffect, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import axios from "axios";
import {
  ChevronLeft,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Target,
  Calendar,
  Clock,
  FileText,
  AlertTriangle,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const KpiReport = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { manager } = location.state || {};

  if (!manager) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="w-96 bg-white shadow-md rounded-lg p-6">
          <div className="">
            <h2 className="text-xl font-semibold">No Data Available</h2>
            <p className="text-gray-600">
              No data was passed for the selected manager.
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      SUBMITTED: {
        bg: "bg-green-100",
        text: "text-green-800",
        icon: CheckCircle2,
      },
      PENDING: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        icon: Clock,
      },
      Undone: {
        bg: "bg-red-100",
        text: "text-red-800",
        icon: AlertTriangle,
      },
    };

    const config = statusConfig[status] || {
      bg: "bg-gray-100",
      text: "text-gray-800",
      icon: Target,
    };

    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}
      >
        <Icon className="w-4 h-4" />
        {status || "In Progress"}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-center space-x-4 pb-2">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <CardTitle>KPI Report</CardTitle>
              <CardDescription>View and manage KPI reports</CardDescription>
            </div>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <CardTitle>{manager.name}</CardTitle>
                <CardDescription>{manager.description}</CardDescription>
              </div>
              {getStatusBadge(manager.status)}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>Start Date: {manager.start_date}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4" />
                <span>Due Date: {manager.due_date}</span>
              </div>
            </div>

            {manager.targets && manager.targets.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Targets</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {manager.targets.map((target, index) => (
                    <Card key={target.id || index}>
                      <CardHeader>
                        <CardTitle className="text-blue-600 text-lg">
                          {target.name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-600">{target.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <KpiReportForm manager={manager} />
      </div>
    </div>
  );
};

const KpiReportForm = ({ manager }) => {
  const apiUrl = import.meta.env.VITE_API_URL;
  const token = JSON.parse(localStorage.getItem("user"))?.access;
  const userId = JSON.parse(localStorage.getItem("user"))?.user_id;
  const [notification, setNotification] = useState(null);
  const totalExpectedRef = useRef([]);
  const reportIdsRef = useRef(null);
  const targetRef = useRef(null);

  const [initialValues, setInitialValues] = useState({
    target_reports: [],
  });

  useEffect(() => {
    const fetchKpis = async () => {
      try {
        const response = await axios.get(
          `${apiUrl}/api/v1/tracker/kpis/reports/all?reported_by=${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const kpiData = response.data.data[0];

        totalExpectedRef.current =
          kpiData.target_reports?.map((report) => report.total_expected) || [];
        targetRef.current =
          kpiData.target_reports?.map((report) => report.name) || [];
        reportIdsRef.current = kpiData.id;

        setInitialValues({
          target_reports:
            kpiData.target_reports?.map((report) => ({
              id: report.id,
              notes: report.notes || "",
              challenges: report.challenges || "",
              actual_completed: report.actual_completed || 0,
            })) || [],
        });
      } catch (error) {
        setNotification({
          type: "error",
          message: "Failed to fetch KPI data. Please try again.",
        });
      }
    };

    fetchKpis();
  }, [apiUrl, token, userId]);

  const validationSchema = Yup.object({
    target_reports: Yup.array().of(
      Yup.object().shape({
        notes: Yup.string(),
        challenges: Yup.string(),
        actual_completed: Yup.number().min(
          0,
          "Actual completed must be at least 0"
        ),
      })
    ),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await axios.put(
        `${apiUrl}/api/v1/tracker/kpis/reports/${reportIdsRef.current}`,
        values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
      setNotification({
        type: "success",
        message: "KPI report submitted successfully!",
      });
      setInitialValues(values);
    } catch (err) {
      setNotification({
        type: "error",
        message:
          err.response?.data?.message || "An error occurred. Please try again.",
      });
    } finally {
      setSubmitting(false);
      setTimeout(() => setNotification(null), 3000);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Target Reports</CardTitle>
          <CardDescription>Update your KPI target reports</CardDescription>
        </CardHeader>
        <CardContent>
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, values }) => (
              <Form className="space-y-6">
                <FieldArray name="target_reports">
                  {({ form }) => (
                    <div className="grid md:grid-cols-2 gap-6">
                      {form.values.target_reports.map((report, index) => (
                        <Card key={index}>
                          <CardHeader>
                            <CardTitle className="text-lg text-blue-600">
                              {targetRef.current[index] ||
                                "Target Name Not Found"}
                            </CardTitle>
                            <CardDescription>
                              Expected Completion:{" "}
                              {totalExpectedRef.current[index] || 0}
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Actual Completed
                              </label>
                              <Field
                                name={`target_reports.${index}.actual_completed`}
                                type="number"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <ErrorMessage
                                name={`target_reports.${index}.actual_completed`}
                                component="div"
                                className="mt-1 text-sm text-red-600"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Notes
                              </label>
                              <Field
                                name={`target_reports.${index}.notes`}
                                as="textarea"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                rows={3}
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Challenges
                              </label>
                              <Field
                                name={`target_reports.${index}.challenges`}
                                as="textarea"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                rows={3}
                              />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </FieldArray>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Submit Report"
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>

      {notification && (
        <div className="fixed bottom-4 right-4 animate-slide-up">
          <Alert
            variant={notification.type === "error" ? "destructive" : "default"}
          >
            {notification.type === "error" ? (
              <AlertCircle className="w-4 h-4" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            <AlertDescription>{notification.message}</AlertDescription>
          </Alert>
        </div>
      )}
    </>
  );
};

export default KpiReport;
